import { QueryClient, type DefaultOptions } from '@tanstack/react-query'

/**
 * Default options for every query/mutation in the app.
 *
 * - `staleTime: 30s` keeps lists fresh in the table sense — they re-fetch on
 *   focus or after 30s, but immediate revisits don't refetch.
 * - `gcTime: 5m` keeps cached data around for 5 minutes after a query becomes
 *   inactive, so back/forward navigation feels instant.
 * - Retries are handled at the axios layer (with refresh + exponential backoff);
 *   TanStack Query retries are kept conservative so we don't double-retry.
 */
const defaultOptions: DefaultOptions = {
  queries: {
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      // Already retried at the axios layer for transient/5xx.
      // Only retry once at the query layer to cover races (e.g. token refresh).
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status && status >= 400 && status < 500) return false
      return failureCount < 1
    },
  },
  mutations: {
    retry: false,
  },
}

export const queryClient = new QueryClient({ defaultOptions })

/**
 * Helper to invalidate a feature's queries by prefix.
 * Convention: query keys are arrays starting with the feature slug:
 *   ['employees', 'list', { page, search }]
 *   ['employees', 'detail', id]
 *
 * Usage:
 *   invalidateFeature('employees')
 */
export function invalidateFeature(feature: string) {
  return queryClient.invalidateQueries({ queryKey: [feature] })
}
